import { Link, createFileRoute } from '@tanstack/react-router'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

export const Route = createFileRoute('/terms-of-service')({
  component: TermsOfServicePage,
})

const partHeadingClass =
  'text-muted-foreground border-border mt-4 border-b pb-2 text-xs font-semibold tracking-wider uppercase'
const sectionHeadingClass = 'text-lg font-semibold tracking-tight'
const subHeadingClass = 'text-base font-semibold tracking-tight'

function TermsOfServicePage() {
  return (
    <main className='bg-background min-h-svh py-8 sm:py-10'>
      <div className='mx-auto w-full max-w-4xl px-4 sm:px-6'>
        <Card>
          <CardHeader className='flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between'>
            <div className='space-y-1'>
              <CardTitle className='text-2xl tracking-tight'>
                Terms of Service
              </CardTitle>
              <p className='text-muted-foreground text-sm'>
                Lumoo Studio AB · Effective April 28, 2026 · Version 1.0
              </p>
            </div>
            <Link
              to='/sign-in'
              className='text-muted-foreground text-sm underline hover:opacity-80'
            >
              Back to sign in
            </Link>
          </CardHeader>

          <CardContent className='space-y-8 text-sm leading-6 sm:text-base sm:leading-7'>
            <section className='space-y-3'>
              <p>
                These Terms of Service (“Terms”) form a binding agreement
                between you and Lumoo Studio AB (trading as “Lumoo”, “we”,
                “us”). By creating an account, accessing, installing, or using
                any part of the Services, you agree to these Terms and to our
                Privacy Policy. If you are entering into these Terms on behalf
                of a company or other legal entity, you represent that you have
                authority to bind that entity, and “you” refers to that entity.
              </p>
              <p>
                These Terms govern all Lumoo products, including Lumoo X
                (enterprise), Lumoo Air (self-serve), and Lumoo Fit (virtual
                try-on SDK). Enterprise customers accessing Lumoo X under an
                Order Form should read Part II and Part III carefully, as those
                sections govern the commercial and access terms specific to
                enterprise engagements.
              </p>
            </section>

            <h2 className={partHeadingClass}>Part I — Foundation</h2>

            <section className='space-y-3'>
              <h3 className={sectionHeadingClass}>1. Definitions</h3>
              <p>The following defined terms apply throughout these Terms:</p>
              <ul className='list-disc space-y-2 pl-5'>
                <li>
                  <span className='font-medium'>“Services”</span> — Lumoo’s
                  websites, web app, Lumoo X enterprise platform, Lumoo Air
                  self-serve platform, Lumoo Fit browser SDK and virtual try-on
                  API, embedded apps (including Shopify apps), plugins, and any
                  related products or documentation.
                </li>
                <li>
                  <span className='font-medium'>“Lumoo X”</span> — the
                  enterprise edition of the Services, accessed under an Order
                  Form, including dedicated workspace infrastructure, advanced
                  AI image generation, brand workflow tooling, and enterprise
                  support.
                </li>
                <li>
                  <span className='font-medium'>“Order Form”</span> — a written
                  order, statement of work, or commercial agreement between
                  Lumoo and a customer that incorporates these Terms and
                  specifies product access, commercial terms, and service
                  levels.
                </li>
                <li>
                  <span className='font-medium'>“Account”</span> — the user
                  account that authenticates you to the Services.
                </li>
                <li>
                  <span className='font-medium'>“Workspace”</span> — a logical
                  container that scopes data, billing, members, and credits. A
                  user may belong to multiple Workspaces. Enterprise Workspaces
                  under Lumoo X are provisioned per Order Form.
                </li>
                <li>
                  <span className='font-medium'>“User Content”</span> — any
                  content you upload, submit, or otherwise make available
                  through the Services, including images, text prompts, product
                  data, brand assets, and garment photography.
                </li>
                <li>
                  <span className='font-medium'>“Generated Output”</span> —
                  content produced by the Services in response to your prompts
                  and User Content, including AI-generated product imagery,
                  virtual try-on composites, and video renders.
                </li>
                <li>
                  <span className='font-medium'>“Credits”</span> — the unit of
                  consumption used to access AI features. Credit allocations for
                  Lumoo X are set in the applicable Order Form.
                </li>
                <li>
                  <span className='font-medium'>“Subscription”</span> — a
                  recurring paid plan providing a monthly or annual allotment of
                  Credits and feature access. Enterprise Subscriptions are
                  governed by Order Forms.
                </li>
                <li>
                  <span className='font-medium'>
                    “Confidential Information”
                  </span>{' '}
                  — non-public information disclosed by one party to the other
                  that is designated as confidential or that reasonably should
                  be understood to be confidential given its nature and the
                  circumstances of disclosure.
                </li>
                <li>
                  <span className='font-medium'>“Personal Data”</span> — any
                  information relating to an identified or identifiable natural
                  person, as defined under applicable data protection law.
                </li>
                <li>
                  <span className='font-medium'>“DPA”</span> — the Data
                  Processing Agreement governing Lumoo’s processing of Personal
                  Data on behalf of customers acting as data controllers,
                  available at privacy@lumoo.com.
                </li>
                <li>
                  <span className='font-medium'>“SLA”</span> — the Service Level
                  Agreement applicable to Lumoo X customers, as set out in
                  Section 24 and the applicable Order Form.
                </li>
                <li>
                  <span className='font-medium'>“AUP”</span> — the Acceptable
                  Use Policy set out in Section 15.
                </li>
              </ul>
            </section>

            <section className='space-y-3'>
              <h3 className={sectionHeadingClass}>
                2. Eligibility and Account Registration
              </h3>
              <p>
                You must be at least 16 years of age (or the minimum age of
                contractual capacity in your jurisdiction) and legally able to
                enter into a binding contract.
              </p>
              <p>
                You must provide accurate registration information and keep it
                current. You are responsible for all activity under your
                Account, including activity by Workspace members and API key
                holders. You must keep your credentials and API keys
                confidential and notify us immediately at hello@lumoo.com of any
                unauthorised use.
              </p>
              <p>
                We may refuse, suspend, or terminate any Account at our
                reasonable discretion, including for breach of these Terms.
              </p>
            </section>

            <section className='space-y-3'>
              <h3 className={sectionHeadingClass}>3. Order of Precedence</h3>
              <p>
                These Terms apply to all customers. Where there is a conflict
                between documents, the order of precedence is:
              </p>
              <ul className='list-disc space-y-2 pl-5'>
                <li>
                  (1) The applicable Order Form (including any product-specific
                  addenda);
                </li>
                <li>(2) The Data Processing Agreement (DPA);</li>
                <li>
                  (3) Any product-specific terms referenced in the Order Form;
                </li>
                <li>(4) These Terms of Service;</li>
                <li>(5) The Acceptable Use Policy (AUP).</li>
              </ul>
              <p>
                In all cases, the document higher in this order prevails to the
                extent of the conflict. Matters not addressed by an Order Form
                are governed by these Terms.
              </p>
            </section>

            <h2 className={partHeadingClass}>Part II — Commercial Terms</h2>

            <section className='space-y-3'>
              <h3 className={sectionHeadingClass}>
                4. Subscriptions, Credits and Billing
              </h3>

              <h4 className={subHeadingClass}>4.1 Plans and Credits</h4>
              <p>
                Paid plans grant a monthly or annual allowance of Credits, plus
                access to certain features. Credits reset at the start of each
                billing cycle and do not roll over unless the Order Form or plan
                description states otherwise. One-time top-up credit packs
                follow the rules described at the time of purchase.
              </p>
              <p>
                Credits have no monetary value, are not refundable for cash, and
                are not transferable except within a Workspace as configured
                in-app.
              </p>

              <h4 className={subHeadingClass}>4.2 Payment</h4>
              <p>
                Payments are processed by Stripe, or by Shopify Billing for
                Shopify-installed customers. By subscribing, you authorise
                recurring charges in accordance with your selected plan,
                including applicable taxes. Prices are denominated as displayed
                at checkout (typically EUR or USD). We may change prices for
                future billing cycles with reasonable prior notice.
              </p>

              <h4 className={subHeadingClass}>
                4.3 Renewals, Cancellations and Refunds
              </h4>
              <p>
                Subscriptions renew automatically until cancelled. You may
                cancel at any time from Settings → Subscription; cancellation
                takes effect at the end of the then-current billing cycle.
                Except where required by law, payments are non-refundable.
              </p>
              <p>
                If you are an EU consumer, you have a right of withdrawal of 14
                days from purchase under Directive 2011/83/EU. You expressly
                request that we begin providing the Services immediately, and
                you acknowledge that your right of withdrawal is lost once the
                Services have been fully performed.
              </p>

              <h4 className={subHeadingClass}>4.4 Failed Payments</h4>
              <p>
                If a payment fails, we may suspend access until the balance is
                settled. Repeated failures may result in plan downgrade or
                Account suspension.
              </p>
            </section>

            <section className='space-y-3'>
              <h3 className={sectionHeadingClass}>
                5. Enterprise Order Forms and Custom Pricing
              </h3>
              <p>
                Lumoo X customers access the Services under an Order Form
                executed between the customer and Lumoo Studio AB. Order Forms
                may specify:
              </p>
              <ul className='list-disc space-y-2 pl-5'>
                <li>
                  fixed or consumption-based pricing, including annual committed
                  spend and overage rates;
                </li>
                <li>
                  custom Credit allocations, including pooled or per-seat
                  models;
                </li>
                <li>
                  product access scope, including which Lumoo X modules are
                  enabled (AI image generation, brand workflow, VTO integration,
                  API access);
                </li>
                <li>
                  payment terms, invoice schedules, and accepted currencies;
                </li>
                <li>
                  any variations to the renewal or cancellation provisions in
                  Section 4.3.
                </li>
              </ul>
              <p>
                Where an Order Form is in place, it governs commercial terms in
                full. These Terms apply to all matters not addressed by the
                Order Form.
              </p>
            </section>

            <section className='space-y-3'>
              <h3 className={sectionHeadingClass}>
                6. Renewals, Cancellations and Refunds (Enterprise)
              </h3>
              <p>
                Enterprise Order Forms specify their own renewal and notice
                periods. Where an Order Form is silent, the defaults in Section
                4.3 apply.
              </p>
              <p>
                Lumoo X annual agreements require written notice of non-renewal
                no less than 60 days before the end of the then-current term,
                unless a shorter period is specified in the Order Form.
              </p>
              <p>
                Termination for cause by either party does not entitle either
                party to a refund of pre-paid fees covering periods before the
                date of valid termination, except where required by law.
              </p>
            </section>

            <section className='space-y-3'>
              <h3 className={sectionHeadingClass}>
                7. Failed Payments and Suspension (Enterprise)
              </h3>
              <p>
                For enterprise customers, if an invoiced amount is not received
                by the due date, Lumoo will issue a written overdue notice.
                Access suspension will not occur until 15 days after the overdue
                notice, except where the overdue amount exceeds 60 days past due
                or where Lumoo has reasonable grounds to believe payment will
                not be made.
              </p>
              <p>
                Lumoo will not suspend access during a good-faith billing
                dispute, provided the customer has notified Lumoo of the dispute
                in writing within 10 days of the invoice date.
              </p>
            </section>

            <h2 className={partHeadingClass}>
              Part III — Access and Licensing
            </h2>

            <section className='space-y-3'>
              <h3 className={sectionHeadingClass}>
                8. Licence to Use the Services
              </h3>
              <p>
                Subject to your compliance with these Terms and payment of
                applicable fees, Lumoo grants you a limited, non-exclusive,
                non-transferable, non-sublicensable, revocable licence to access
                and use the Services for your internal business purposes. All
                rights not expressly granted are reserved.
              </p>
              <p>
                Lumoo X customers may use the Services to produce Generated
                Outputs for use in their own commercial operations, including
                product imagery for digital storefronts, advertising, and
                wholesale collateral, within the scope described in the
                applicable Order Form.
              </p>
            </section>

            <section className='space-y-3'>
              <h3 className={sectionHeadingClass}>
                9. Workspaces, Roles and Permissions
              </h3>
              <p>
                Workspaces strictly scope data via account identifiers. Members
                of a Workspace can access and operate on Workspace data
                according to their assigned role: owner, admin, member, or
                partner.
              </p>
              <p>
                Owners are responsible for managing members, billing, and
                compliance with these Terms. Inviting a member into a Workspace
                authorises them to access Workspace data.
              </p>
              <p>
                Enterprise Workspaces under Lumoo X are provisioned per Order
                Form and may include custom role configurations, SSO
                integration, and audit log access.
              </p>
            </section>

            <section className='space-y-3'>
              <h3 className={sectionHeadingClass}>
                10. API Keys and Integrations (Shopify, SDK, Plugins)
              </h3>
              <p>
                API keys are Workspace-scoped and consumption is billed against
                your Credits. You are liable for all consumption made with your
                API keys until revocation. Treat API keys with the same care as
                passwords.
              </p>
              <p>
                When you install our Shopify apps, you authorise Lumoo to access
                the data necessary to provide the integration (products, images,
                store metadata) under the scopes approved at install.
              </p>
              <p>
                The Lumoo Fit browser SDK reads product data on the page where
                it is embedded. You are responsible for ensuring the SDK is
                deployed only in contexts where you have a lawful basis to
                process the data involved.
              </p>
              <p>
                Rate limits, quotas, and lifetime credit limits may apply to API
                access, as documented in-app or in your Order Form.
              </p>
            </section>

            <h2 className={partHeadingClass}>Part IV — Content and AI</h2>

            <section className='space-y-3'>
              <h3 className={sectionHeadingClass}>
                11. User Content — Ownership and Licence Grant
              </h3>
              <p>
                You retain all rights in your User Content. You grant Lumoo a
                worldwide, non-exclusive, royalty-free, sublicensable licence to
                host, store, transmit, modify (including resizing, transcoding,
                masking, and compositing), and process your User Content solely
                to provide the Services to you and your Workspace members.
              </p>
              <p>
                You represent and warrant that you have all necessary rights to
                upload your User Content and that it does not infringe
                third-party rights, applicable law, or the AUP.
              </p>
              <p>
                You are solely responsible for backing up your User Content.
                Lumoo maintains backups for operational reasons, but the
                Services are not a backup service.
              </p>
            </section>

            <section className='space-y-3'>
              <h3 className={sectionHeadingClass}>
                12. Generated Outputs — Ownership and Restrictions
              </h3>
              <p>
                Subject to payment of applicable fees and compliance with these
                Terms, you own the Generated Outputs you produce, to the extent
                permitted by law. You may use them for commercial and
                non-commercial purposes consistent with these Terms.
              </p>
              <p>
                Because Generated Outputs are produced by AI models, they may
                not be eligible for copyright protection in some jurisdictions.
                Lumoo makes no warranty as to copyrightability or uniqueness.
                Different users may receive similar outputs from similar
                prompts.
              </p>
              <p>
                You are responsible for verifying that any use of Generated
                Outputs — including on storefronts, in advertising, on
                packaging, or in wholesale catalogues — complies with applicable
                law, including consumer protection, advertising standards, IP,
                and image rights.
              </p>
              <p>
                Lumoo retains a limited right to retain Generated Outputs for
                service operation, abuse monitoring, and as described in the
                Privacy Policy.
              </p>
            </section>

            <section className='space-y-3'>
              <h3 className={sectionHeadingClass}>
                13. AI Features — Disclosures, Limitations and Model Changes
              </h3>
              <p>
                Generated Outputs are produced by third-party AI models (see
                Privacy Policy §5.2). Lumoo does not guarantee accuracy, fitness
                for purpose, originality, non-infringement, or absence of errors
                or artefacts in any Generated Output.
              </p>
              <p>
                Outputs may reflect biases of underlying models. You must
                independently review outputs before use, particularly where they
                depict identifiable people, products, sizes, fits, prices, or
                factual claims.
              </p>
              <p>
                Lumoo may apply automated and manual moderation to inputs and
                outputs. We may refuse to generate, watermark, or remove content
                that violates the AUP or applicable law.
              </p>
              <p>
                Some features depend on third-party AI providers. Lumoo may
                change models, parameters, or pricing at any time to maintain
                quality and operational economics. Where a model change
                materially affects Lumoo X customers’ workflows, Lumoo will
                provide reasonable prior notice as set out in Section 26.
              </p>
            </section>

            <section className='space-y-3'>
              <h3 className={sectionHeadingClass}>
                14. Virtual Try-On (Lumoo Fit) — Disclaimer
              </h3>
              <p>
                Lumoo Fit virtual try-on results are visual approximations of
                how a garment may appear on a given body type or model image.
                They are not size advice, and Lumoo makes no representation that
                outputs accurately reflect garment dimensions, fabric drape, or
                fit for any individual.
              </p>
              <p>
                Customers deploying Lumoo Fit are solely responsible for the
                accuracy of size charts, fit guides, and any size
                recommendations displayed to end consumers. Lumoo Fit outputs
                must not be presented as a substitute for accurate product
                measurements.
              </p>
            </section>

            <h2 className={partHeadingClass}>
              Part V — Compliance and Acceptable Use
            </h2>

            <section className='space-y-3'>
              <h3 className={sectionHeadingClass}>15. Acceptable Use Policy</h3>
              <p>You agree not to use the Services to:</p>
              <ul className='list-disc space-y-2 pl-5'>
                <li>
                  violate any law, regulation, or third-party right, including
                  intellectual property and privacy rights;
                </li>
                <li>
                  generate, upload, or distribute content that is illegal,
                  abusive, threatening, harassing, defamatory, or that incites
                  violence or hatred;
                </li>
                <li>
                  generate sexual content involving minors, or any content that
                  sexualises minors — Lumoo will report such content to relevant
                  authorities (including NCMEC) where required by law;
                </li>
                <li>
                  generate non-consensual intimate imagery, non-consensual
                  deepfakes of real persons, or content used for harassment,
                  defamation, or fraud;
                </li>
                <li>
                  impersonate any real person or entity, or misrepresent the
                  origin of Generated Outputs in a deceptive manner;
                </li>
                <li>
                  generate content designed to deceive in elections, public
                  health, financial markets, or that is otherwise intended to
                  cause public harm;
                </li>
                <li>
                  reverse engineer, decompile, scrape, probe, or attempt to
                  discover source code, model weights, or system prompts, except
                  to the extent permitted by mandatory law;
                </li>
                <li>
                  circumvent rate limits, abuse-prevention systems, watermarks,
                  moderation, or authentication;
                </li>
                <li>
                  resell, sublicense, or wrap the Services to provide a
                  substantially similar competitive product, except under
                  Lumoo’s written API or reseller terms;
                </li>
                <li>
                  use the Services to train competing AI models on Generated
                  Outputs, prompts, or platform behaviour;
                </li>
                <li>
                  upload malware, conduct penetration testing without Lumoo’s
                  written permission, or interfere with the integrity or
                  performance of the Services.
                </li>
              </ul>
              <p>
                Lumoo may suspend or terminate Accounts and remove content for
                violations of this policy.
              </p>
            </section>

            <section className='space-y-3'>
              <h3 className={sectionHeadingClass}>
                16. Moderation, Watermarking and Enforcement
              </h3>
              <p>
                Lumoo applies automated content filtering to inputs and outputs.
                Inputs that match prohibited content categories are blocked
                prior to generation. Outputs that trigger post-generation
                moderation may be withheld, watermarked, or flagged for manual
                review.
              </p>
              <p>
                Enterprise customers may request information about Lumoo’s
                moderation approach under their Order Form. Lumoo will not
                disable safety systems as a condition of any commercial
                agreement.
              </p>
            </section>

            <section className='space-y-3'>
              <h3 className={sectionHeadingClass}>
                17. Export Controls and Sanctions Compliance
              </h3>
              <p>
                You may not use the Services in violation of applicable export
                control laws or regulations, including those of the European
                Union, the United States (including EAR and OFAC regulations),
                and Sweden.
              </p>
              <p>
                You represent and warrant that: (a) you are not located in, or a
                national or resident of, any country subject to a comprehensive
                embargo; (b) you are not on any denied parties list maintained
                by a relevant government authority; and (c) you will not use the
                Services to produce content for export to embargoed destinations
                or for prohibited end uses.
              </p>
              <p>
                Lumoo reserves the right to suspend or terminate access
                immediately, without liability, where continued access would
                breach applicable export control or sanctions obligations.
              </p>
            </section>

            <section className='space-y-3'>
              <h3 className={sectionHeadingClass}>
                18. Intellectual Property and DMCA/DSA Notices
              </h3>
              <p>
                The Services, including all software, design, trademarks, logos,
                system prompts, and documentation, are owned by Lumoo or its
                licensors and are protected by applicable intellectual property
                laws. Nothing in these Terms transfers ownership of any Lumoo IP
                to you.
              </p>
              <p>
                Feedback you provide may be used by Lumoo without obligation, on
                a non-confidential basis.
              </p>
              <p>
                Lumoo respects intellectual property rights and responds to
                valid infringement notices under the EU Digital Services Act and
                the US DMCA. Send notices to hello@lumoo.com.
              </p>
            </section>

            <h2 className={partHeadingClass}>Part VI — Data and Privacy</h2>

            <section className='space-y-3'>
              <h3 className={sectionHeadingClass}>
                19. Privacy and Personal Data Processing
              </h3>
              <p>
                Lumoo’s processing of personal data in the course of providing
                the Services is described in our Privacy Policy, available at
                lumoo.com/privacy. The Privacy Policy applies to all users of
                the Services.
              </p>
              <p>
                For business customers acting as data controllers in respect of
                personal data uploaded to the Services, the DPA applies and is
                incorporated into these Terms by reference. The DPA is available
                at privacy@lumoo.com and incorporates the EU Standard
                Contractual Clauses and the UK International Data Transfer
                Addendum where applicable.
              </p>
            </section>

            <section className='space-y-3'>
              <h3 className={sectionHeadingClass}>
                20. Data Processing Agreement (DPA)
              </h3>
              <p>
                Where Lumoo processes Personal Data on your behalf as a data
                processor (within the meaning of the GDPR or equivalent
                applicable law), the parties are subject to the DPA. The DPA
                governs:
              </p>
              <ul className='list-disc space-y-2 pl-5'>
                <li>
                  the subject matter, nature, purpose, and duration of the
                  processing;
                </li>
                <li>
                  the types of Personal Data processed and categories of data
                  subjects;
                </li>
                <li>the obligations and rights of the data controller;</li>
                <li>
                  Lumoo’s obligations as data processor, including
                  confidentiality, security measures, and sub-processor
                  management;
                </li>
                <li>data subject rights and cooperation obligations;</li>
                <li>
                  transfers of Personal Data outside the European Economic Area,
                  including under Standard Contractual Clauses.
                </li>
              </ul>
              <p>
                The DPA is available upon request at privacy@lumoo.com. For
                Lumoo X customers, execution of a DPA is required prior to the
                commencement of processing.
              </p>
            </section>

            <section className='space-y-3'>
              <h3 className={sectionHeadingClass}>
                21. Data Security and Breach Notification
              </h3>
              <p>
                Lumoo maintains technical and organisational security measures
                appropriate to the risk of processing, including encryption at
                rest and in transit, access controls, penetration testing, and
                incident response procedures. Details of Lumoo’s security
                controls are available to enterprise customers upon written
                request.
              </p>
              <p>
                In the event of a personal data breach affecting customer data,
                Lumoo will:
              </p>
              <ul className='list-disc space-y-2 pl-5'>
                <li>
                  notify the customer without undue delay and, where feasible,
                  within 72 hours of becoming aware of the breach;
                </li>
                <li>
                  provide in the initial notice, or as soon as practicable
                  thereafter: the nature of the breach; the categories and
                  approximate number of data subjects and records concerned; the
                  likely consequences; and measures taken or proposed to address
                  the breach;
                </li>
                <li>
                  cooperate with the customer’s breach response and regulatory
                  notification obligations as required by applicable law.
                </li>
              </ul>
              <p>
                The customer remains responsible for its own obligations to
                notify supervisory authorities and data subjects under
                applicable law.
              </p>
            </section>

            <section className='space-y-3'>
              <h3 className={sectionHeadingClass}>
                22. Sub-processors and Third-Party AI Providers
              </h3>
              <p>
                Lumoo uses sub-processors to deliver the Services, including
                third-party AI model providers, cloud infrastructure providers,
                and payment processors. A current list of sub-processors is
                maintained at lumoo.com/sub-processors and updated as changes
                are made.
              </p>
              <p>
                Lumoo will notify enterprise customers of material changes to
                sub-processors — including additions or replacements of AI model
                providers — with at least 30 days’ prior notice. Enterprise
                customers who have a legitimate objection to a new sub-processor
                may raise it in writing within 14 days of notice. If the
                objection cannot be resolved, the customer may terminate the
                affected Services without penalty.
              </p>
              <p>
                Lumoo’s current AI model infrastructure includes third-party
                providers for image generation, virtual try-on, and related AI
                features. These providers process User Content and prompts as
                necessary to produce Generated Outputs. Lumoo contractually
                requires these providers to maintain appropriate data processing
                and security obligations.
              </p>
            </section>

            <h2 className={partHeadingClass}>
              Part VII — Service Levels and Support
            </h2>

            <section className='space-y-3'>
              <h3 className={sectionHeadingClass}>
                23. Service Availability and Maintenance
              </h3>
              <p>
                Lumoo targets high availability for all Services but does not
                guarantee uninterrupted access. Scheduled maintenance windows,
                third-party provider outages (including AI infrastructure), and
                unforeseen events may cause interruptions.
              </p>
              <p>
                Lumoo will provide advance notice of scheduled maintenance via
                in-app notice or email where reasonably practicable. Emergency
                maintenance may be performed without advance notice where
                necessary to protect the security or integrity of the Services.
              </p>
            </section>

            <section className='space-y-3'>
              <h3 className={sectionHeadingClass}>
                24. Enterprise Support and SLAs
              </h3>
              <p>
                Enterprise customers accessing Lumoo X under an Order Form
                receive support in accordance with the service level terms set
                out in that Order Form. Where the Order Form is silent, the
                following baseline terms apply.
              </p>

              <h4 className={subHeadingClass}>24.1 Uptime Target</h4>
              <p>
                Lumoo targets 99.5% monthly uptime for Lumoo X platform
                availability, excluding scheduled maintenance and force majeure
                events. Uptime is measured as the percentage of minutes in a
                calendar month during which the core platform is accessible.
              </p>

              <h4 className={subHeadingClass}>24.2 Response Times</h4>
              <p>Support requests are categorised as follows:</p>
              <ul className='list-disc space-y-2 pl-5'>
                <li>
                  <span className='font-medium'>P1 (Critical)</span> — platform
                  unavailable or data loss risk: initial response within 2
                  business hours; status update every 4 hours until resolved.
                </li>
                <li>
                  <span className='font-medium'>P2 (High)</span> — major feature
                  impaired with no workaround: initial response within 1
                  business day; resolution target within 3 business days.
                </li>
                <li>
                  <span className='font-medium'>P3 (Medium)</span> — feature
                  impaired with workaround available: initial response within 2
                  business days; resolution target within 10 business days.
                </li>
                <li>
                  <span className='font-medium'>P4 (Low)</span> — general
                  queries, feature requests, non-urgent issues: initial response
                  within 3 business days.
                </li>
              </ul>
              <p>
                Business hours are Monday–Friday, 09:00–18:00 CET, excluding
                Swedish public holidays.
              </p>

              <h4 className={subHeadingClass}>24.3 Service Credits</h4>
              <p>
                Where Lumoo fails to meet the uptime target in any calendar
                month, the customer is entitled to a service credit as follows:
              </p>
              <ul className='list-disc space-y-2 pl-5'>
                <li>
                  <span className='font-medium'>Uptime 99.0%–99.4%</span>:
                  credit equal to 5% of that month’s fees.
                </li>
                <li>
                  <span className='font-medium'>Uptime 95.0%–98.9%</span>:
                  credit equal to 10% of that month’s fees.
                </li>
                <li>
                  <span className='font-medium'>Uptime below 95.0%</span>:
                  credit equal to 25% of that month’s fees.
                </li>
              </ul>
              <p>
                Credits are applied to the next invoice and are not redeemable
                for cash. Service credits are the customer’s sole remedy for
                availability failures, except where such failures constitute a
                material breach entitling termination under Section 19. To claim
                a credit, the customer must submit a written request within 15
                days of the end of the relevant month.
              </p>

              <h4 className={subHeadingClass}>24.4 Dedicated Support</h4>
              <p>
                Enterprise customers under Lumoo X receive a named account
                manager and access to a dedicated Slack or email channel for
                support escalation, as specified in the Order Form. Standard
                support for all other customers is provided via hello@lumoo.com.
              </p>
            </section>

            <section className='space-y-3'>
              <h3 className={sectionHeadingClass}>
                25. Beta and Experimental Features
              </h3>
              <p>
                Features designated as ‘Beta’, ‘Experimental’, ‘Preview’, or
                similar are provided as-is without any warranty and may be
                modified or discontinued at any time without notice. Use at your
                own risk. Lumoo X customers may access certain preview features
                under pilot terms specified in their Order Form.
              </p>
            </section>

            <section className='space-y-3'>
              <h3 className={sectionHeadingClass}>
                26. Feature Changes and Deprecation Notice
              </h3>
              <p>
                Lumoo may add, modify, or remove features at any time. For
                changes that materially reduce the value of a paid plan or
                enterprise Order Form, Lumoo will provide at least 60 days’
                written notice to affected enterprise customers before the
                change takes effect.
              </p>
              <p>
                For AI model changes that materially affect generation quality
                or workflow compatibility for Lumoo X customers, Lumoo will
                provide at least 30 days’ notice and, where practicable, access
                to a parallel preview environment during the transition period.
              </p>
              <p>
                Where a material feature removal is not remedied within a
                reasonable period, and the feature was a material basis for the
                customer’s Order Form, the customer may terminate the affected
                subscription for cause under Section 19 without penalty.
              </p>
            </section>

            <h2 className={partHeadingClass}>
              Part VIII — Liability and Indemnity
            </h2>

            <section className='space-y-3'>
              <h3 className={sectionHeadingClass}>27. Confidentiality</h3>
              <p>
                Each party agrees to: (a) protect the other party’s Confidential
                Information using at least the same degree of care it uses to
                protect its own confidential information (and no less than
                reasonable care); (b) use Confidential Information only as
                necessary to perform under these Terms; and (c) disclose
                Confidential Information only to employees, contractors, and
                advisors who need to know it and who are bound by
                confidentiality obligations no less protective than these Terms.
              </p>
              <p>
                Confidential Information does not include information that: (i)
                was publicly available at the time of disclosure through no
                fault of the receiving party; (ii) was rightfully known to the
                receiving party without restriction prior to disclosure; (iii)
                was independently developed by the receiving party without use
                of Confidential Information; or (iv) is required to be disclosed
                by law or regulation, provided the receiving party gives prompt
                prior written notice and cooperates with reasonable efforts to
                obtain confidential treatment.
              </p>
              <p>
                The confidentiality obligations in this section survive
                termination of these Terms for a period of three (3) years.
              </p>
            </section>

            <section className='space-y-3'>
              <h3 className={sectionHeadingClass}>28. Disclaimers</h3>
              <p className='uppercase'>
                To the maximum extent permitted by applicable law, the Services
                and all Generated Outputs are provided “as is” and “as
                available”, without warranties of any kind, express or implied,
                including warranties of merchantability, fitness for a
                particular purpose, non-infringement, accuracy, or that the
                Services will be error-free or uninterrupted.
              </p>
              <p className='uppercase'>
                Nothing in these Terms excludes or limits liability that cannot
                be excluded or limited by law, including mandatory consumer
                rights where applicable.
              </p>
            </section>

            <section className='space-y-3'>
              <h3 className={sectionHeadingClass}>
                29. Limitation of Liability
              </h3>
              <p className='uppercase'>
                To the maximum extent permitted by applicable law:
              </p>
              <ul className='list-disc space-y-2 pl-5'>
                <li>
                  Neither party is liable for indirect, incidental, special,
                  consequential, exemplary, or punitive damages, or for loss of
                  profits, revenues, goodwill, or data, even if advised of the
                  possibility of such damages.
                </li>
                <li>
                  Lumoo’s total aggregate liability arising out of or relating
                  to these Terms or the Services shall not exceed the greater
                  of: (a) the amount paid by the customer in the twelve (12)
                  months preceding the event giving rise to the claim; or (b)
                  EUR 100.
                </li>
              </ul>
              <p>
                These limitations do not apply to: (i) liability for death or
                personal injury caused by negligence; (ii) fraud or fraudulent
                misrepresentation; (iii) wilful misconduct; or (iv) any other
                liability that cannot be excluded under applicable law.
              </p>
              <p>
                <span className='font-medium'>Lumoo X customers:</span> the EUR
                100 floor in (b) is typically negotiated upward in the Order
                Form to reflect the enterprise contract value. Review your Order
                Form for the applicable cap.
              </p>
            </section>

            <section className='space-y-3'>
              <h3 className={sectionHeadingClass}>
                30. Mutual Indemnification
              </h3>

              <h4 className={subHeadingClass}>30.1 Customer Indemnity</h4>
              <p>
                The customer will defend, indemnify, and hold harmless Lumoo and
                its affiliates, officers, directors, employees, and agents from
                and against third-party claims, damages, liabilities, costs, and
                expenses (including reasonable legal fees) arising from: (a) the
                customer’s User Content; (b) the customer’s use of Generated
                Outputs; (c) the customer’s breach of these Terms or violation
                of applicable law; or (d) the customer’s violation of
                third-party rights, including intellectual property, privacy,
                and publicity rights.
              </p>

              <h4 className={subHeadingClass}>30.2 Lumoo Indemnity</h4>
              <p>
                Lumoo will defend, indemnify, and hold harmless the customer and
                its affiliates, officers, directors, employees, and agents from
                and against third-party claims alleging that the Services
                (excluding Generated Outputs, User Content, and third-party AI
                model outputs), as provided by Lumoo and used in accordance with
                these Terms, infringe that third party’s intellectual property
                rights.
              </p>
              <p>
                Lumoo’s indemnity obligations are subject to: (i) the customer
                providing prompt written notice of the claim; (ii) Lumoo having
                sole control of the defence and settlement (provided no
                settlement imposes liability on the customer without its
                consent); and (iii) the customer providing reasonable
                cooperation at Lumoo’s expense.
              </p>
              <p>
                If a claim covered by Lumoo’s indemnity arises, Lumoo may, at
                its option: (a) procure the right for the customer to continue
                using the Services; (b) modify the Services to make them
                non-infringing; or (c) terminate the affected Services and
                refund pre-paid fees for the remaining term.
              </p>
            </section>

            <section className='space-y-3'>
              <h3 className={sectionHeadingClass}>31. Insurance</h3>
              <p>
                During the term of any active Order Form, Lumoo will maintain
                the following insurance coverage:
              </p>
              <ul className='list-disc space-y-2 pl-5'>
                <li>
                  <span className='font-medium'>
                    Professional indemnity / errors and omissions
                  </span>
                  : not less than EUR 1,000,000 per occurrence;
                </li>
                <li>
                  <span className='font-medium'>Cyber liability</span>: not less
                  than EUR 1,000,000 per occurrence;
                </li>
                <li>
                  <span className='font-medium'>General liability</span>: not
                  less than EUR 1,000,000 per occurrence.
                </li>
              </ul>
              <p>
                Lumoo will provide certificates of insurance to enterprise
                customers upon written request. Coverage amounts may be varied
                by mutual agreement in the Order Form.
              </p>
            </section>

            <h2 className={partHeadingClass}>Part IX — Term and Disputes</h2>

            <section className='space-y-3'>
              <h3 className={sectionHeadingClass}>
                32. Term, Suspension and Termination
              </h3>
              <p>
                These Terms apply from the date you create an Account and
                continue until terminated. Enterprise Order Forms have the term
                specified in the Order Form.
              </p>
              <p>
                Either party may terminate for cause immediately upon written
                notice if the other party: (a) materially breaches these Terms
                and fails to cure the breach within 30 days of written notice;
                (b) becomes insolvent, makes an assignment for the benefit of
                creditors, or a liquidation or bankruptcy proceeding is
                commenced; or (c) in the case of the customer, violates the AUP.
              </p>
              <p>
                Lumoo may also suspend or terminate an Account immediately for
                risk to the Services or other users, non-payment (subject to
                Section 7), or where required by applicable law or regulatory
                authority.
              </p>
              <p>
                Sections that by their nature survive termination will survive,
                including Sections 11, 12, 17, 18, 27, 28, 29, 30, 33, 35, and
                36.
              </p>
            </section>

            <section className='space-y-3'>
              <h3 className={sectionHeadingClass}>
                33. Effect of Termination and Data Return
              </h3>
              <p>Upon termination or expiry of these Terms or an Order Form:</p>
              <ul className='list-disc space-y-2 pl-5'>
                <li>
                  access to the Services will cease at the end of the applicable
                  period;
                </li>
                <li>
                  Lumoo will make the customer’s User Content available for
                  export for 30 days following the termination date, in a
                  standard machine-readable format (JSON or CSV for structured
                  data; original file formats for media);
                </li>
                <li>
                  after the 30-day export window, Lumoo will delete or anonymise
                  the customer’s User Content and Personal Data in accordance
                  with the DPA and Privacy Policy, except where Lumoo is
                  required to retain data by applicable law;
                </li>
                <li>
                  each party will return or destroy the other party’s
                  Confidential Information upon request, subject to legal
                  retention requirements.
                </li>
              </ul>
              <p>
                Customers are solely responsible for exporting their data before
                the export window closes.
              </p>
            </section>

            <section className='space-y-3'>
              <h3 className={sectionHeadingClass}>34. Force Majeure</h3>
              <p>
                Neither party is liable for failure or delay in performance
                caused by events beyond its reasonable control, including acts
                of God, war, terrorism, pandemics, civil unrest, governmental
                action, internet or third-party service outages, AI provider
                outages, or labour disputes. The affected party must notify the
                other promptly and use reasonable efforts to mitigate the
                impact.
              </p>
              <p>
                If a force majeure event affecting Lumoo’s ability to deliver
                the Services continues for more than 30 consecutive days, either
                party may terminate the affected Order Form without penalty, and
                Lumoo will refund pre-paid fees for the unfulfilled portion of
                the term.
              </p>
            </section>

            <section className='space-y-3'>
              <h3 className={sectionHeadingClass}>
                35. Governing Law and Jurisdiction
              </h3>
              <p>
                These Terms are governed by the laws of Sweden, excluding its
                conflict-of-law rules and the UN Convention on Contracts for the
                International Sale of Goods. The courts of Sweden have exclusive
                jurisdiction over disputes arising from these Terms, subject to
                any mandatory consumer protection laws of the customer’s country
                of residence.
              </p>
              <p>
                EU consumers may also access the European Commission’s Online
                Dispute Resolution platform at{' '}
                <a
                  href='https://ec.europa.eu/consumers/odr'
                  target='_blank'
                  rel='noreferrer'
                  className='underline hover:opacity-80'
                >
                  https://ec.europa.eu/consumers/odr
                </a>
                .
              </p>
            </section>

            <section className='space-y-3'>
              <h3 className={sectionHeadingClass}>
                36. Dispute Resolution and Escalation
              </h3>
              <p>
                Before initiating formal legal proceedings, the parties will
                attempt in good faith to resolve any dispute by negotiation.
                Either party may initiate this process by giving written notice
                describing the dispute in reasonable detail.
              </p>
              <p>
                If the dispute is not resolved within 30 days of the written
                notice, either party may escalate as follows:
              </p>
              <ul className='list-disc space-y-2 pl-5'>
                <li>
                  for enterprise customers under an active Order Form: a senior
                  management escalation meeting within 10 business days,
                  attended by a named representative from each party with
                  authority to settle;
                </li>
                <li>
                  if the escalation meeting does not resolve the dispute within
                  a further 30 days: formal proceedings before the courts of
                  Sweden as set out in Section 35.
                </li>
              </ul>
              <p>
                Nothing in this section prevents either party from seeking
                urgent injunctive or equitable relief before the appropriate
                courts where necessary to protect its rights.
              </p>
            </section>

            <h2 className={partHeadingClass}>Part X — General</h2>

            <section className='space-y-3'>
              <h3 className={sectionHeadingClass}>
                37. Changes to These Terms
              </h3>
              <p>
                Lumoo may modify these Terms from time to time. For material
                changes, Lumoo will give at least 30 days’ notice by in-app
                notice or email before the changes take effect. Your continued
                use of the Services after the effective date constitutes
                acceptance. If you do not agree, you must stop using the
                Services and may terminate your Account.
              </p>
              <p>
                For enterprise customers under an active Order Form, material
                changes to these Terms do not take effect during the current
                Order Form term without the customer’s written consent, unless
                required by applicable law.
              </p>
            </section>

            <section className='space-y-3'>
              <h3 className={sectionHeadingClass}>
                38. Assignment and Change of Control
              </h3>
              <p>
                The customer may not assign these Terms or any Order Form
                without Lumoo’s prior written consent. Lumoo may assign these
                Terms in connection with a merger, acquisition, corporate
                reorganisation, or sale of all or substantially all of its
                assets, provided that the assignee assumes all obligations under
                these Terms.
              </p>
              <p>
                If a change of control of Lumoo results in the Services being
                acquired by a direct competitor of the customer, the customer
                may terminate the applicable Order Form without penalty within
                60 days of receiving notice of the change of control, and Lumoo
                will refund pre-paid fees for the remaining term.
              </p>
            </section>

            <section className='space-y-3'>
              <h3 className={sectionHeadingClass}>
                39. Entire Agreement and Order of Precedence
              </h3>
              <p>
                These Terms, together with the Privacy Policy, the DPA (where
                applicable), any Order Forms, and any product-specific terms
                referenced therein, constitute the entire agreement between the
                parties on this subject matter and supersede all prior
                agreements, representations, and understandings. The order of
                precedence in Section 3 applies.
              </p>
            </section>

            <section className='space-y-3'>
              <h3 className={sectionHeadingClass}>40. Miscellaneous</h3>
              <p>
                <span className='font-medium'>Severability:</span> if any
                provision of these Terms is found unenforceable, it will be
                modified to the minimum extent necessary to make it enforceable,
                and the remaining provisions continue in full force.
              </p>
              <p>
                <span className='font-medium'>No waiver:</span> failure by
                either party to enforce any right or provision is not a waiver
                of that right or provision.
              </p>
              <p>
                <span className='font-medium'>Notices:</span> legal notices to
                Lumoo must be sent to Malmskillnadsgatan 36, 111 51 Stockholm,
                Sweden, with a copy to hello@lumoo.com. Lumoo may give notices
                to customers via in-app message or email to the address on the
                Account.
              </p>
              <p>
                <span className='font-medium'>Independent contractors:</span>{' '}
                nothing in these Terms creates a partnership, joint venture,
                employment, or agency relationship between the parties.
              </p>
              <p>
                <span className='font-medium'>Language:</span> these Terms are
                written in English. Where a translation is provided for
                convenience, the English version governs.
              </p>
            </section>

            <section className='space-y-3'>
              <h3 className={sectionHeadingClass}>
                41. Contact and Legal Notices
              </h3>
              <p>
                <span className='font-medium'>General enquiries:</span>{' '}
                hello@lumoo.com
                <br />
                <span className='font-medium'>Privacy and DPA:</span>{' '}
                privacy@lumoo.com
                <br />
                <span className='font-medium'>
                  IP / DMCA / DSA notices:
                </span>{' '}
                hello@lumoo.com
                <br />
                <span className='font-medium'>Registered address:</span> Lumoo
                Studio AB, Malmskillnadsgatan 36, 111 51 Stockholm, Sweden
                (Epicenter)
              </p>
            </section>

            <p className='text-muted-foreground border-border border-t pt-4 text-xs'>
              © 2026 Lumoo Studio AB. All rights reserved. Terms of Service
              v1.0.
            </p>
          </CardContent>
        </Card>
      </div>
    </main>
  )
}
